import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query'
import fetch from '../common/fetch'
import { queryClient } from '../common/react-query/queryClient'
import { useQueryWithLocalStoragePersist } from '../common/react-query/useQueryWithLocalStoragePersist'
import { PrimaryKey } from '../common/types'
import { Log } from '../settings/settingsApi'

export const api = {
  getCurrencies: (): Promise<Currency[]> => fetch.get(`/api/currency/`),
  getCurrency: async (currencyCode: string): Promise<Currency | null> => {
    const allCurrencies: Currency[] = await fetch.get('/api/currency/')
    const matchedCurrency = allCurrencies.filter(c => c.code === currencyCode)
    return matchedCurrency.length ? matchedCurrency[0] : null
  },
  getCurrencyActivityLogs: (): Promise<Log[]> => fetch.get(`/api/currency/activity_logs/`),
  getSystemCurrencyOptions: (): Promise<CurrencyOption[]> => {
    return fetch.get(`/api/v2/settings/currencies/`)
  },
  getCurrencySettings: (): Promise<CurrencySettings> => fetch.get(`/api/currency/currency_settings/`),
  saveEntitySettings: (values: CurrencySettings) => fetch.put(`/api/currency/currency_settings/`, values),
  createOrUpdateCurrency: (values: Currency) =>
    values.id ? fetch.patch(`/api/currency/${values.id}/`, values) : fetch.post(`/api/currency/`, values),
  getClientCurrencyOptions: (): Promise<CurrencyOption[]> => fetch.get(`/api/currency/options/`),
  getClientCurrencyOptionsForWorksite: (worksiteId: PrimaryKey): Promise<CurrencyOption[]> =>
    fetch.get(`/api/currency/options/${worksiteId}`)
}

export interface Currency {
  id?: number
  code: string
  name?: string
  decimal_places: number
  is_active: boolean
  multiplier_to_base_currency: number
  updated_at?: string
}

export interface CurrencyOption {
  label: string
  value: string
}

export interface CurrencySettings {
  base_currency_code: string
  multi_currency_enabled: boolean
}

export const useCurrencySettingsQuery = () => {
  return useQuery({ queryKey: ['currency', 'settings'], queryFn: () => api.getCurrencySettings(), staleTime: Infinity })
}

export const useSystemCurrencyOptionsQuery = () => {
  return useQuery({
    queryKey: ['currency', 'systemOptions'],
    queryFn: () => api.getSystemCurrencyOptions(),
    staleTime: Infinity
  })
}

export const useCurrencyLogsQuery = ({ initialData }: { initialData?: Log[] } = {}) => {
  return useQuery({ queryKey: ['currency', 'activityLogs'], queryFn: () => api.getCurrencyActivityLogs(), initialData })
}

export const useCurrencyQuery = (currencyCode: string) => {
  return useQuery({
    queryKey: ['currency', currencyCode],
    queryFn: () => api.getCurrency(currencyCode),
    staleTime: Infinity
  })
}

export const useCurrenciesQuery = () => {
  return useQueryWithLocalStoragePersist(['currency', 'list'] as const, () => api.getCurrencies(), {
    staleTime: Infinity,
    localStorageKey: 'currencies_data',
    preventClientServerMismatches: true
  })
}

export const useCurrenciesQueryNoLocalStorage = () => {
  return useQuery({ queryKey: ['currency', 'list'], queryFn: () => api.getCurrencies(), staleTime: Infinity })
}

export const useSaveSettingsMutation = (opts?: UseMutationOptions<void, unknown, CurrencySettings>) =>
  useMutation({
    mutationFn: (values: CurrencySettings) => api.saveEntitySettings(values),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries(['currency', 'settings'])
      await queryClient.invalidateQueries(['currency', 'activityLogs'])
      opts?.onSuccess?.(...args)
    }
  })

export const useCurrencyCreateOrUpdateMutation = (opts: UseMutationOptions<any, unknown, Currency>) =>
  useMutation({
    mutationFn: (values: Currency) => api.createOrUpdateCurrency(values),
    ...opts,
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries(['currency'])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const useCurrencyOptionsQuery = ({ worksite }: { worksite?: PrimaryKey } = {}) => {
  const currencySettingsQ = useCurrencySettingsQuery()
  const currencyOptionsQ = useQuery({
    queryKey: ['currency', 'clientOptions', worksite],
    queryFn: () => {
      if (worksite) return api.getClientCurrencyOptionsForWorksite(worksite)
      return api.getClientCurrencyOptions()
    }
  })

  /*
   * The above useQuery calls two different api's depending on whether worksite
   * is passed in or not.  Those two different api's have different return
   * signatures so we are making them look similar below
   */
  return worksite
    ? currencyOptionsQ // This already has default_currency and options
    : {
        // This branch does not so we are making it look similar
        data: {
          default_currency: currencySettingsQ?.data?.base_currency_code,
          options: currencyOptionsQ?.data
        },
        isLoading: currencyOptionsQ.isLoading || currencySettingsQ.isLoading,
        isInitialLoading: currencyOptionsQ.isInitialLoading || currencySettingsQ.isInitialLoading
      }
}
